import foodImg from '@assets/landing/section4/food_img.png';
// Assets
import payBgImg from '@assets/landing/section4/pay_bg_img.png';
import photoRound from '@assets/landing/section4/photo_round.png';
import transactBg from '@assets/landing/section4/transact_bg.png';
import logo from '@assets/logo.svg';
import { Box } from '@components/ui/atoms/Box';
import { Container } from '@components/ui/atoms/Container';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Image } from '@components/ui/atoms/Image';
import { Text } from '@components/ui/atoms/Text';
import {
  fadeInScale,
  floatHover10,
  floatHover12Reverse,
  slideUpFade,
} from '@shared/utils/animationsUtil';
import { useScrollReveal } from '@shared/utils/useScrollReveal';
import React from 'react';

export const PaySection: React.FC = () => {
  const { isVisible, ref: sectionRef } = useScrollReveal(0.1);

  return (
    <Box as="section" bg="white" overflow="hidden" ref={sectionRef}>
      {/* --- Section 1: Top Header --- */}
      <Container maxW="container.xl" pt="5rem" pb="1rem">
        <Flex align="center" gap="1.5rem">
          <Text
            fontSize="xs"
            fontWeight="500"
            color="gray.400"
            letterSpacing="0.05em"
            whiteSpace="nowrap"
          >
            GET PAID
            <Text as="span" color="#3B26B1" fontWeight="700" ml="0.5rem">
              QUICKLY WITH INSTANT CONFIRMATION
            </Text>
          </Text>
          <Box flex="1" h="1px" bg="gray.100" />
        </Flex>
      </Container>

      {/* --- Section 2: Hero Content Area --- */}
      <Box position="relative" minH={{ base: '1100px', lg: '1200px' }} w="full">
        {/* Background Image (The Man) */}
        <Box
          position="absolute"
          width="100%"
          height="100%"
          zIndex="0"
          display="flex"
          justifyContent="center"
          alignItems="flex-start"
          pt="2rem"
        >
          <Image
            src={payBgImg}
            alt="Pay Section Background"
            w="100%"
            h="100%"
            objectFit={{ base: 'none', md: 'cover' }}
            opacity="0.95"
          />
        </Box>

        {/* Floating Elements Container */}
        <Container maxW="container.xl" position="relative" h="full" zIndex="1">
          {/* Decorative Square (Behind Linked Accounts Card) */}
          <Box
            position="absolute"
            top={{ base: '200px', md: '12%' }}
            right={{ base: '8%', md: '26%' }}
            w="90px"
            h="90px"
            bg="#E3EDFF"
            borderRadius="lg"
            opacity={0}
            animation={
              isVisible
                ? `${slideUpFade} 0.8s ease-out forwards 0.6s, ${floatHover12Reverse} 7s ease-in-out infinite 1.4s`
                : 'none'
            }
          />
          {/* Toast Top-Left */}
          <Box
            position="absolute"
            top={{ base: '85px', md: '65px' }}
            left="2%"
            bg="rgba(211, 221, 250, 0.6)"
            backdropFilter="blur(12px)"
            p={{ base: '1rem', md: '1.5rem' }}
            borderRadius="xl"
            boxShadow="0 15px 35px rgba(0,0,0,0.05)"
            maxW="340px"
            opacity={0}
            animation={
              isVisible
                ? `${slideUpFade} 0.6s ease-out forwards 0.4s, ${floatHover10} 5s ease-in-out infinite 1s`
                : 'none'
            }
          >
            <Flex align="center" gap="1rem">
              <Box bg="blue.900" borderRadius="full" p="0.5rem" w="50px" h="36px">
                <Image src={logo} w={5} h={5} alt="logo" />
              </Box>
              <Box>
                <Text fontSize="13px" fontWeight="500" color="#556987" lineHeight="1.6">
                  You received a payout! Funds will credit your account within 24 hours.
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Round Photo (Precisely positioned near man's head) */}
          <Box
            position="absolute"
            top="12%"
            left={{ base: '240px', md: '320px' }}
            w="130px"
            h="130px"
            borderRadius="full"
            overflow="hidden"
            opacity={0}
            animation={
              isVisible
                ? `${fadeInScale} 0.6s ease-out forwards 0.7s, ${floatHover12Reverse} 8s ease-in-out infinite 1.3s`
                : 'none'
            }
          >
            <Image src={photoRound} alt="User" w="full" h="full" objectFit="cover" />
          </Box>

          {/* Purple Info Card */}
          <Box
            position="absolute"
            top={{ base: '420px', md: '550px' }}
            left="6%"
            bg="#423D70"
            p={{ base: '20px', md: '40px' }}
            borderRadius="2xl"
            boxShadow="0 40px 80px rgba(0,0,0,0.25)"
            maxW="500px"
            mr={{ base: '6px', md: '0' }}
            color="white"
            opacity={0}
            animation={isVisible ? `${slideUpFade} 0.8s ease-out forwards 0.5s` : 'none'}
          >
            <Heading
              fontSize={{ base: '20px', md: '22px' }}
              mb={{ base: '0.75rem', md: '1.75rem' }}
              fontWeight="700"
              letterSpacing="-0.01em"
            >
              Choose Where You Receive Funds
            </Heading>
            <Text
              fontSize={{ base: '12px', md: '14px' }}
              opacity="0.85"
              lineHeight="1.8"
              color="#D9D9EE"
            >
              Manage how you receive funds with flexible payout configuration. Connect and manage
              multiple bank accounts for payouts, assign accounts based on use case, and keep your
              finances organized as your operations grow. You get reliable processing with simple,
              configurable payout options—without added complexity.
            </Text>
          </Box>

          {/* Center-Right Toast (Check details) */}
          <Box
            position="absolute"
            top={{ base: '800px', md: '550px' }}
            right={{ base: '25%', md: '18%' }}
            bg="rgb(186 243 255 / 45%)"
            backdropFilter="blur(20px)"
            p={{ base: '1rem', md: '1.5rem' }}
            ml={{ base: '6px', md: '0' }}
            borderRadius="xl"
            boxShadow="0 15px 35px rgba(0,0,0,0.04)"
            maxW={{ base: '280px', md: '400px' }}
            border="1px solid rgba(255,255,255,0.2)"
            zIndex={2}
            opacity={0}
            animation={
              isVisible
                ? `${slideUpFade} 0.6s ease-out forwards 0.8s, ${floatHover10} 6s ease-in-out infinite 1.4s`
                : 'none'
            }
          >
            <Flex gap={{ base: '0.75rem', md: '1.25rem' }} align="center">
              <Flex
                bg="blue.900"
                borderRadius="full"
                w={{ base: '36px', md: '40px' }}
                h={{ base: '36px', md: '40px' }}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Image src={logo} w={{ base: 4, md: 5 }} h={{ base: 4, md: 5 }} alt="logo" />
              </Flex>
              <Box>
                <Text
                  fontSize={{ base: '12px', md: '13px' }}
                  fontWeight="500"
                  color="#3B4E6C"
                  lineHeight="1.6"
                >
                  No need to stop at the bank! Your funds have been disbursed to your 2 linked
                  accounts.
                </Text>
              </Box>
            </Flex>
          </Box>

          {/* Linked Accounts Card */}
          <Box
            position="absolute"
            top={{ base: '626px', md: '350px' }}
            right="3%"
            bg="white"
            borderRadius="2xl"
            boxShadow="0 25px 60px rgba(0,0,0,0.1)"
            w="320px"
            overflow="hidden"
            p={{ base: '20px', md: '30px' }}
            zIndex={1}
            opacity={0}
            animation={
              isVisible
                ? `${slideUpFade} 0.8s ease-out forwards 0.9s, ${floatHover12Reverse} 7s ease-in-out infinite 1.7s`
                : 'none'
            }
          >
            <Flex direction="column" gap="1.25rem">
              <Flex align="center" gap="1rem">
                <Box
                  bg="#3B26B1"
                  borderRadius="full"
                  p="4px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Box>
                <Text fontSize="13px" fontWeight="600" color="#2D3748">
                  My Linked Account #1
                </Text>
                <Text fontSize="13px" fontWeight="700" color="#1A202C" ml="auto">
                  $1,480.00
                </Text>
              </Flex>
              <Flex align="center" gap="1rem">
                <Box
                  bg="#3B26B1"
                  borderRadius="full"
                  p="4px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Box>
                <Text fontSize="13px" fontWeight="600" color="#2D3748">
                  My Linked Account #2
                </Text>
                <Text fontSize="13px" fontWeight="700" color="#1A202C" ml="auto">
                  $59.99
                </Text>
              </Flex>

              <Box borderRadius="xl" overflow="hidden" mt="0.5rem">
                <Image src={foodImg} alt="Transaction" w="full" h="150px" objectFit="cover" />
              </Box>

              <Box mt="0.5rem">
                <Text fontSize="11px" color="gray.500" lineHeight="1.5">
                  All payouts are related to Transfer TR-123ABC.
                </Text>
                <Text
                  fontSize="12px"
                  color="#3B26B1"
                  fontWeight="700"
                  mt="6px"
                  cursor="pointer"
                  textDecoration="none"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Check details
                </Text>
              </Box>
            </Flex>
          </Box>
        </Container>
      </Box>

      {/* --- Section 3: Bottom Call to Action --- */}
      <Box
        backgroundColor="#C3D0FF"
        backgroundImage={`url(${transactBg})`}
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundBlendMode="multiply"
        py={{ base: '8rem', md: '12rem' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        mt="-8rem"
        position="relative"
        zIndex="2"
      >
        <Container
          maxW="container.md"
          opacity={isVisible ? 1 : 0}
          animation={isVisible ? `${slideUpFade} 0.8s ease-out forwards 1.2s` : 'none'}
        >
          <Heading
            fontSize={{ base: '24px', md: '38px' }}
            fontWeight="600"
            color="#332E90"
            lineHeight="1.35"
            maxW="850px"
            mx="auto"
          >
            Why settle for the ordinary when you can build on a payments platform designed for
            simplicity, speed, and reliability? Join us and redefine the way you transact.
          </Heading>
        </Container>
      </Box>
    </Box>
  );
};

export default PaySection;

PaySection.displayName = 'PaySection';
